import React from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { useRegistrationData } from 'hooks/queries/users/useRegistrationData'
import { useBrandProfile, fetchBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import {
  fetchBrandVisualEnhancement,
  useBrandVisualEnhancement,
} from 'hooks/queries/brandVisualEnhancements/useBrandVisualEnhancement'
import { useOnboard } from 'hooks/queries/useOnboard'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useModals } from 'almanac-ui/Modal'
import { useFlow } from 'flows/common/hooks/useFlow'
import { onboardingSteps } from '../onboarding/steps'
import { useOnboardingStrategiesEnabled, useVirtualMarketerRedesignSteps } from '../virtualMarketerOnboarding/steps'
import { BLAZE_HOME_PATH, LOGIN_PATH, SCHEDULED_POSTS_PATH } from 'paths'
import { vmRegistrationSteps } from 'screens/Registration/RegistrationAndOnboarding'
import { useSession } from 'hooks/useSession'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { useQueryClient } from 'react-query'
import { RegistrationData, UserClient } from 'api/clients/UserClient'
import { AIJobStatus } from 'types'
import { useCampaignsFeatureFlag } from 'hooks/useCampaignsFeatureFlag'

export const useRegistrationFlow = () => {
  const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled()
  const history = useHistory()
  const auth = useAuth()
  const hasAuth = Boolean(auth)
  const { logout } = useSession()
  const queryClient = useQueryClient()

  const { data: regResp } = useRegistrationData({ enabled: hasAuth })

  const { data: brandProfile } = useBrandProfile({ enabled: hasAuth })

  const isCampaignsEnabled = useCampaignsFeatureFlag()

  const { data: brandVisualEnhancement } = useBrandVisualEnhancement(
    brandProfile?.brand_kit?.brand_visual_kit?.id,
    brandProfile?.brand_kit?.brand_visual_kit?.brand_visual_enhancement_id ?? undefined,
    {
      enabled: hasAuth && !!brandProfile?.brand_kit?.brand_visual_kit?.brand_visual_enhancement_id,
    },
  )

  const { mutateAsync: onboard } = useOnboard()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { closeAllModals } = useModals()

  const computeStart = React.useCallback((): string => {
    if (!hasAuth) return 'registration'

    if (auth?.last_workspace_id === ALMANAC_CORE_WORKSPACE_ID || auth?.company_id === ALMANAC_CORE_WORKSPACE_ID) {
      logout({ logoutRedirect: LOGIN_PATH })
    }

    const reg = regResp?.user_registration_data ?? null
    const brandVisualEnhancementStatus = brandVisualEnhancement?.brand_visual_enhancement?.status
    const brandPlan = brandProfile?.brand_plan
    const hasTopics =
      (brandPlan?.twitter_topics && brandPlan.twitter_topics.length > 0) ||
      (brandPlan?.email_topics && brandPlan.email_topics.length > 0) ||
      (brandPlan?.facebook_topics && brandPlan.facebook_topics.length > 0) ||
      (brandPlan?.instagram_topics && brandPlan.instagram_topics.length > 0) ||
      (brandPlan?.linkedin_topics && brandPlan.linkedin_topics.length > 0) ||
      (brandPlan?.seo_topics && brandPlan.seo_topics.length > 0)

    if (!auth?.onboarded && !reg?.business_type) return 'businessType'
    if (!auth?.onboarded && reg?.marketing_experience_level == null) return 'marketingExperience'
    if (!auth?.onboarded && !brandProfile) return 'buildYourPlan'
    if (!auth?.onboarded && hasTopics) return 'awareness'
    if (
      !auth?.onboarded &&
      brandVisualEnhancementStatus &&
      [AIJobStatus.Pending, AIJobStatus.Started].includes(brandVisualEnhancementStatus)
    ) {
      return 'enhanceBrandFonts'
    }
    if (!auth?.onboarded && brandVisualEnhancementStatus) {
      return 'brandKitConfirm'
    }
    if (!auth?.onboarded && !!brandPlan) return 'brandKitConfirm'
    if (!auth?.onboarded && brandProfile) return 'profile'

    return 'businessType'
  }, [auth, brandProfile, hasAuth, logout, regResp, brandVisualEnhancement])

  const virtualMarketerSteps = useVirtualMarketerRedesignSteps()
  const steps = React.useMemo(
    () => [...vmRegistrationSteps, ...onboardingSteps, ...virtualMarketerSteps],
    [virtualMarketerSteps],
  )

  const handleComplete = async () => {
    await onboard()
    closeAllModals()

    let targetPath = generatePath(BLAZE_HOME_PATH, { workspaceId: currentWorkspaceId })
    if (isOnboardingStrategiesEnabled) {
      targetPath = generatePath(SCHEDULED_POSTS_PATH, { workspaceId: currentWorkspaceId, view: 'calendar' })
    }

    history.push(targetPath)
  }

  /**
   *  Replacement for `ensureQueryData` (RQ v4)
   *  TODO: remove this once we migrate to RQ v4.
   */
  const getOrFetch = <T>(key: any, fn: () => Promise<T>) => {
    const cached = queryClient.getQueryData<T>(key)
    return cached ? Promise.resolve(cached) : queryClient.fetchQuery(key, fn)
  }

  const flow = useFlow({
    id: 'fullSignupFlow',
    steps,
    fetchInitialData: async () => {
      if (!hasAuth) {
        return {
          auth: null,
          regData: null,
          initialState: 'registration',
        }
      }

      const results = await Promise.all([
        getOrFetch('userRegistrationData', () => UserClient.getRegistrationData()),
        getOrFetch(['workspaces', currentWorkspaceId, 'brandProfile'], () =>
          fetchBrandProfile({ workspaceId: currentWorkspaceId }),
        ),
      ])

      const fetchedProfile = results[1]

      if (
        fetchedProfile?.brand_kit?.brand_visual_kit?.brand_visual_enhancement_id &&
        fetchedProfile?.brand_kit?.brand_visual_kit?.id
      ) {
        const enhancementId = fetchedProfile.brand_kit.brand_visual_kit.brand_visual_enhancement_id
        const visualKitId = fetchedProfile.brand_kit.brand_visual_kit.id

        await getOrFetch(['brandVisualEnhancement', enhancementId], () =>
          fetchBrandVisualEnhancement({
            id: enhancementId,
            brandVisualKitId: visualKitId,
            workspaceId: currentWorkspaceId,
          }),
        )
      }

      return {
        auth,
        regData: queryClient.getQueryData('userRegistrationData') as RegistrationData | null,
        initialState: computeStart(),
        brandProfileEnhancementEnabled: true,
        isBrandPlanStrategiesEnabled: isCampaignsEnabled,
        brandProfile: fetchedProfile,
        brandKit: fetchedProfile?.brand_kit,
        brandKitId: fetchedProfile?.brand_kit?.id,
        brandPlanId: fetchedProfile?.brand_plan?.id,
        brandProfileId: fetchedProfile?.id,
        analysisId: fetchedProfile?.latest_analysis_id,
        brandVisualEnhancementId: fetchedProfile?.brand_kit?.brand_visual_kit?.brand_visual_enhancement_id,
        wantsToEnhance: !fetchedProfile?.brand_kit?.brand_visual_kit?.brand_visual_enhancement_id,
      }
    },
    onComplete: handleComplete,
  })

  const resetFlow = React.useCallback(() => flow.send({ type: 'RESET' }), [flow])

  return { ...flow, resetFlow }
}
